/**
 * Booking Settings data — the one hook the three-pane screen reads through.
 *
 * Data: bound (preview: seed-world via the serving engine) — reads
 * getBookingSettings · listBookingMessageTypes · listBookingTemplates; writes
 * updateBookingSettings · createBookingTemplate · updateBookingTemplate ·
 * setBookingTemplateState · restoreBookingTemplateVersion. Request lifecycle
 * revised 2026-10-18 (see readGate below); writes bound to the confirmed scope
 * they started in (r2, same day); a late rejection is normalised the same way as a
 * late success (r4, same day). Ops and payloads unchanged.
 * Every read and write is a typed op of the booking-settings family
 * (`@v0y4ge/api-client`), served in preview by the engine from seed-world.
 * Nothing here is data; it is the shape of asking for it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
/* NAMESPACE import, resolved at CALL time (2026-09-09).
 *
 * A static `import { getBookingSettings, ... } from "@v0y4ge/api-client"` is what
 * this file had, and it crashed the route. The eight booking-settings ops are
 * absent from the kit — not in the client's src, not in its dist — so the
 * preview's loader barrel-patched them into permissive async stubs. Each stub
 * resolves to a value with no `data`, `setTypes(ty.data)` stored `undefined`,
 * and `typeById` then called `.find` on it: "Cannot read properties of undefined
 * (reading 'find')", straight into the error boundary for every persona on
 * `messaging.appointments`.
 *
 * Same pattern as `useDiscounts.ts`, `ms-graph-api.ts` and now
 * `confirmation-pages-api.ts`: take the namespace, look the op up when it is
 * called, and when it is absent say so instead of pretending. The screen then
 * degrades to an honest unavailable state and self-heals the moment the kit
 * carries the family — no edit here.
 *
 * The contract half already exists (`api-contracts/src/booking-settings.ts`, with
 * a full operations table); what is missing is the barrel export, the built
 * `dist/`, and the eight client wrappers. Reported in
 * `cc-outbox/2026-09-09-kit-receipt-4ad74e1f.md`. */
import * as client from "@v0y4ge/api-client";
import type {
  BookingSettings, BookingMessageType, BookingTemplate, BookingTemplateState,
  UpdateBookingSettingsInput, UpdateBookingTemplateInput, CreateBookingTemplateInput,
} from "@v0y4ge/api-contracts";
import { useTenant, getConfirmedTenantSlug, getTenantGeneration, onTenantSwitching, onTenantChange, getTenantIdentityStatus, retryTenantIdentity } from "../../../shell/access";
import { t as i18nT } from "../../../lib/i18n";

export type { BookingSettings, BookingMessageType, BookingTemplate, BookingTemplateState };

export const BS_OPS = [
  "getBookingSettings", "updateBookingSettings", "listBookingMessageTypes", "listBookingTemplates",
  "createBookingTemplate", "updateBookingTemplate", "setBookingTemplateState", "restoreBookingTemplateVersion",
] as const;

type Op = (typeof BS_OPS)[number];

/** Message shown where the family is not served. Names the ops so a reader can
 *  check the claim rather than take it. */
export const BS_UNAVAILABLE =
  "Booking Settings needs the booking-settings operations, which this build's API client does not carry. "
  + "Nothing is shown rather than showing values that are not real.";

/** Resolve an op off the namespace at call time. A barrel-patched stub is NOT a
 *  real op — it is a function that answers a shape nobody wrote — so anything
 *  that is not a function is treated as absent. */
function op<T>(name: Op): ((...a: never[]) => Promise<T>) | null {
  const f = (client as unknown as Record<string, unknown>)[name];
  return typeof f === "function" ? (f as (...a: never[]) => Promise<T>) : null;
}

/** Every op present = the family is served here. */
export const BS_SERVED: boolean = BS_OPS.every((n) => op(n) !== null);

/** Absent op, called anyway. Throws rather than resolving — a write that cannot
 *  reach the server must not report success. */
function absent(name: Op): never {
  throw new Error(`${name} is not available in this build (${BS_UNAVAILABLE})`);
}

/* ── Read lifecycle ───────────────────────────────────────────────────
 * The screen's three reads run as ONE parallel trio per confirmed tenant scope.
 * The scope comes from shell/access.ts — the confirmed slug (null before whoami
 * resolves and for the whole of a pending workspace switch) plus the tenant
 * generation (moves on every confirmed switch or rollback). That is the same
 * pair customers-data.ts keys its scoped reads on.
 *
 * What it replaces: the old effect keyed on useTenant()'s slug, which is null on
 * first render. It fired one trio with no tenant, then a second when the slug
 * arrived, and either answer could land last and win. Now no trio starts until
 * the scope is confirmed, a second start for the same scope joins the one in
 * flight, and every success, error and finally checks it is still the newest
 * run, for the same scope, on a mounted screen before it touches state. */
export interface ReadScope { slug: string | null; generation: number }
export interface ReadRun { id: number; key: string; slug: string; current: () => boolean }
/** A write's binding to the confirmed scope it started in. `live()` is false
 *  once that scope has moved (pending switch, confirmed switch, rollback) or the
 *  screen unmounted. */
export interface WriteTicket { key: string; slug: string; live: () => boolean }
export interface ReadGate {
  /** Initial load for `key`: null when that scope already has a trio in flight
   *  or already loaded. `force` (Retry, refresh) always starts a newer run. */
  begin: (key: string, slug: string, force: boolean) => ReadRun | null;
  /** The run finished, whatever the outcome. Frees the in-flight slot if it was the newest. */
  settle: (run: ReadRun, loaded: boolean) => void;
  /** Supersede everything in flight without starting anything (tenant switch begins). */
  invalidate: () => void;
  /** Mounted state. Unmount stops every pending callback from committing. */
  setAlive: (alive: boolean) => void;
  /** Bind a write to the current confirmed scope; null when there is none. */
  ticket: () => WriteTicket | null;
}

export function scopeKey(scope: ReadScope): string | null {
  return scope.slug ? scope.slug + "#" + String(scope.generation) : null;
}

export function createReadGate(readScope: () => ReadScope): ReadGate {
  let latest = 0;
  let alive = true;
  let inflightKey: string | null = null;
  let loadedKey: string | null = null;
  return {
    begin(key: string, slug: string, force: boolean): ReadRun | null {
      if (!force && (inflightKey === key || loadedKey === key)) return null;
      latest += 1;
      const id = latest;
      inflightKey = key;
      return { id, key, slug, current: () => alive && id === latest && scopeKey(readScope()) === key };
    },
    settle(run: ReadRun, loaded: boolean): void {
      if (run.id !== latest) return;
      inflightKey = null;
      if (loaded) loadedKey = run.key;
    },
    invalidate(): void { latest += 1; inflightKey = null; loadedKey = null; },
    setAlive(v: boolean): void { alive = v; },
    ticket(): WriteTicket | null {
      const scope = readScope();
      const key = scopeKey(scope);
      if (!key || !scope.slug) return null;
      return { key, slug: scope.slug, live: () => alive && scopeKey(readScope()) === key };
    },
  };
}

/* ── Write scope ──────────────────────────────────────────────────────
 * Every write is bound to a ticket. It is checked before the request is sent
 * and again after the answer arrives. A write with no confirmed scope is refused
 * before anything is sent (SCOPE_UNCONFIRMED). A write whose scope moved while it
 * was in flight REJECTS with SCOPE_CHANGED instead of resolving, so a caller
 * running several writes in sequence stops there and never sends the next one
 * under the new tenant. The request already on the wire cannot be recalled; it
 * was sent under the scope it was bound to. */
export type BookingScopeCode = "SCOPE_UNCONFIRMED" | "SCOPE_CHANGED";
export class BookingScopeError extends Error {
  readonly code: BookingScopeCode;
  constructor(code: BookingScopeCode) {
    super(code === "SCOPE_UNCONFIRMED"
      ? i18nT("bookingSettings.scope.unconfirmed", "Your workspace isn't confirmed yet, so nothing was saved. Try again in a moment.")
      : i18nT("bookingSettings.scope.changed", "The workspace changed while this was saving."));
    this.name = "BookingScopeError";
    this.code = code;
  }
}
/** True for a write that was abandoned because its scope moved. Callers stay silent on it. */
export function isObsoleteWrite(e: unknown): boolean {
  return e instanceof BookingScopeError && e.code === "SCOPE_CHANGED";
}
/** Await a request bound to `ticket`. If the ticket died while the request was in
 *  flight, the outcome is SCOPE_CHANGED whether the request resolved OR rejected.
 *  A late failure from the old scope does not surface as its own error in the new
 *  one. While the ticket is live, the real result or the real error passes through. */
export async function settleInScope<R>(ticket: WriteTicket, request: Promise<R>): Promise<R> {
  let r: R;
  try { r = await request; }
  catch (e) { if (!ticket.live()) throw new BookingScopeError("SCOPE_CHANGED"); throw e; }
  if (!ticket.live()) throw new BookingScopeError("SCOPE_CHANGED");
  return r;
}

/** Turn whatever a write threw into its outcome at THIS boundary. Once the ticket
 *  is dead, any error (the original request failure included) is SCOPE_CHANGED.
 *  Each await crosses a microtask boundary where a switch can land, so this is
 *  applied again at every caller catch, not only inside settleInScope. */
export function scopedError(ticket: WriteTicket, e: unknown): unknown {
  if (isObsoleteWrite(e)) return e;
  return ticket.live() ? e : new BookingScopeError("SCOPE_CHANGED");
}

export type ActionOutcome = "ok" | "failed" | "obsolete";
export interface ActionCallbacks<R> {
  /** The work finished and the ticket is still live at this continuation. */
  ok?: (r: R) => void;
  /** A real failure in the same scope (SCOPE_UNCONFIRMED included: nothing was sent). */
  failed: (e: unknown) => void;
  /** Same-scope end of the action, after ok/failed. Not called for an obsolete run. */
  settled?: () => void;
}
/** A single screen action bound end to end to the ticket taken when it was pressed.
 *  The ticket is checked in the component's own continuation, directly before each
 *  callback, so a switch that lands after the data layer's last check still
 *  silences the toast, draft change and finally. Never rejects. */
export async function runInScope<R>(
  ticket: WriteTicket | null,
  work: (t: WriteTicket) => Promise<R>,
  on: ActionCallbacks<R>,
): Promise<ActionOutcome> {
  if (!ticket) { on.failed(new BookingScopeError("SCOPE_UNCONFIRMED")); on.settled?.(); return "failed"; }
  let outcome: ActionOutcome = "obsolete";
  let result: R;
  try {
    result = await work(ticket);
  } catch (e) {
    const err = scopedError(ticket, e);
    if (isObsoleteWrite(err)) return outcome;
    outcome = "failed";
    on.failed(err);
    if (ticket.live()) on.settled?.();
    return outcome;
  }
  if (!ticket.live()) return outcome;
  outcome = "ok";
  on.ok?.(result);
  if (ticket.live()) on.settled?.();
  return outcome;
}

export type PublishOutcome = "published" | "failed" | "obsolete";
export interface PublishWriters<D> {
  saveTemplate: (id: string, patch: D, ticket: WriteTicket) => Promise<unknown>;
  saveSettings: (patch: UpdateBookingSettingsInput, ticket: WriteTicket) => Promise<unknown>;
}
export interface PublishCallbacks {
  /** Every draft was written and the scope is still the one publish started in. */
  done: () => void;
  /** A real failure in the same scope. Not called for an obsolete run. */
  failed: (e: unknown) => void;
  /** Same-scope end of the run, after done/failed. Not called for an obsolete run. */
  settled: () => void;
}
/** The Publish loop, bound end to end to one ticket. Checks the ticket before each
 *  write and after each await; once it is dead, sends nothing more and calls none
 *  of done/failed/settled. Drafts are the snapshot taken when Publish was pressed. */
export async function publishDrafts<D extends UpdateBookingTemplateInput>(
  ticket: WriteTicket,
  drafts: Record<string, D>,
  settingsPatch: UpdateBookingSettingsInput | null,
  writers: PublishWriters<D>,
  on: PublishCallbacks,
): Promise<PublishOutcome> {
  let outcome: PublishOutcome = "obsolete";
  try {
    for (const [id, patch] of Object.entries(drafts)) {
      if (!ticket.live()) return outcome;
      await writers.saveTemplate(id, patch, ticket);
    }
    if (settingsPatch) {
      if (!ticket.live()) return outcome;
      await writers.saveSettings(settingsPatch, ticket);
    }
    if (!ticket.live()) return outcome;
    outcome = "published";
    on.done();
  } catch (e) {
    const err = scopedError(ticket, e);
    if (isObsoleteWrite(err)) return outcome;
    outcome = "failed";
    on.failed(err);
  } finally {
    if (outcome !== "obsolete" && ticket.live()) on.settled();
  }
  return outcome;
}

const confirmedScope = (): ReadScope => ({ slug: getConfirmedTenantSlug(), generation: getTenantGeneration() });

/** Where tenant identity stands for this screen. "pending" covers the first
 *  whoami and a workspace switch in progress; "failed"/"tenantless" are terminal
 *  until Retry (access.ts retryTenantIdentity). */
export type BookingIdentity = "ready" | "pending" | "failed" | "tenantless";

export interface BookingSettingsData {
  /** No usable data yet for this scope: waiting on identity, or the first trio is in flight. */
  loading: boolean;
  /** A trio is re-reading on top of data already shown (Retry, refresh). */
  refreshing: boolean;
  error: string | null;
  /** The last read for this scope failed but the previous answer is still on screen. */
  stale: boolean;
  identity: BookingIdentity;
  /** Confirmed tenant the data on screen belongs to; null while unresolved or switching. */
  tenantSlug: string | null;
  /** Current confirmed scope (slug#generation); null while unresolved or switching. */
  scopeKey: string | null;
  /** Writes may start: scope confirmed and the data on screen was read for it. */
  writable: boolean;
  /** Ticket for a multi-step write (Publish); null when `writable` is false. */
  beginWrite: () => WriteTicket | null;
  settings: BookingSettings | null;
  types: BookingMessageType[];
  templates: BookingTemplate[];
  /** Retry/refresh. When identity failed, re-asks for identity instead; no trio until it resolves. */
  refresh: () => Promise<void>;
  /* Each write takes an optional ticket. Without one it binds to the scope current
     at the call. It rejects with BookingScopeError rather than send outside it. */
  saveSettings: (patch: UpdateBookingSettingsInput, ticket?: WriteTicket) => Promise<void>;
  saveTemplate: (id: string, patch: UpdateBookingTemplateInput, ticket?: WriteTicket) => Promise<BookingTemplate>;
  createTemplate: (input: CreateBookingTemplateInput, ticket?: WriteTicket) => Promise<BookingTemplate>;
  setState: (id: string, state: BookingTemplateState, ticket?: WriteTicket) => Promise<{ template: BookingTemplate; archived: string[] }>;
  restore: (id: string, version: number, ticket?: WriteTicket) => Promise<BookingTemplate>;
  /** The live template for a type in the default locale — what the type pane edits. */
  liveFor: (typeId: string) => BookingTemplate | null;
  typeById: (id: string) => BookingMessageType | null;
}

export const BS_IDENTITY_FAILED = i18nT("bookingSettings.identity.failed", "We couldn't confirm which workspace you're signed in to, so nothing was loaded.");
export const BS_IDENTITY_TENANTLESS = i18nT("bookingSettings.identity.tenantless", "Your session didn't include a workspace, so nothing was loaded.");

export function useBookingSettings(): BookingSettingsData {
  // Re-renders when whoami resolves or fails; the scope itself is read from the confirmed pair.
  useTenant();
  const [, bump] = useState(0);
  const scope = confirmedScope();
  const key = scopeKey(scope);
  const keyRef = useRef(key);
  keyRef.current = key;
  const idStatus = getTenantIdentityStatus();
  const identity: BookingIdentity = key ? "ready" : idStatus === "failed" ? "failed" : idStatus === "tenantless" ? "tenantless" : "pending";

  const gateRef = useRef<ReadGate | null>(null);
  if (!gateRef.current) gateRef.current = createReadGate(confirmedScope);
  const gate = gateRef.current;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<BookingSettings | null>(null);
  const [types, setTypes] = useState<BookingMessageType[]>([]);
  const [templates, setTemplates] = useState<BookingTemplate[]>([]);
  /** Which tenant the held data was read for. Same slug = same-scope refresh. */
  const [dataSlug, setDataSlug] = useState<string | null>(null);
  const dataSlugRef = useRef<string | null>(null);

  const purge = useCallback(() => {
    setSettings(null); setTypes([]); setTemplates([]);
    setDataSlug(null); dataSlugRef.current = null;
  }, []);

  const load = useCallback(async (force: boolean) => {
    if (!BS_SERVED) {
      /* Say it once, plainly, and leave the collections EMPTY rather than
         undefined — the crash was an `undefined.find()`, and a list that is
         empty renders as "nothing here" instead of throwing. No request. */
      purge(); setError(BS_UNAVAILABLE); setLoading(false); setRefreshing(false);
      return;
    }
    const k = keyRef.current;
    const slug = getConfirmedTenantSlug();
    if (!k || !slug) return; // identity not confirmed: no trio
    const run = gate.begin(k, slug, force);
    if (!run) return; // this scope's trio is already in flight or already answered
    const sameScope = dataSlugRef.current === slug;
    if (!sameScope) purge();
    if (sameScope) setRefreshing(true); else setLoading(true);
    let loaded = false;
    try {
      const readSettings = op<{ data: BookingSettings }>("getBookingSettings");
      const readTypes = op<{ data: BookingMessageType[] }>("listBookingMessageTypes");
      const readTemplates = op<{ data: BookingTemplate[] }>("listBookingTemplates");
      const [s, ty, tp] = await Promise.all([readSettings!(), readTypes!(), readTemplates!()]);
      if (!run.current()) return;
      /* Defensive even on the served path: a response that arrives without its
         array is a contract violation, and the screen should show nothing rather
         than crash on it. */
      setSettings(s?.data ?? null);
      setTypes(Array.isArray(ty?.data) ? ty.data : []);
      setTemplates(Array.isArray(tp?.data) ? tp.data : []);
      setDataSlug(run.slug); dataSlugRef.current = run.slug;
      setError(null);
      loaded = true;
    } catch (e) {
      if (!run.current()) return;
      /* Same scope: keep what is on screen and say the re-read failed (stale).
         Another scope's data was already purged above. */
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      const mine = run.current();
      gate.settle(run, loaded);
      if (mine) { setLoading(false); setRefreshing(false); }
    }
  }, [gate, purge]);

  // Mount/unmount. A remount of the same instance (StrictMode) re-arms the same gate.
  useEffect(() => { gate.setAlive(true); return () => gate.setAlive(false); }, [gate]);

  // Switch begins: drop the outgoing tenant's data now, start nothing until it is confirmed.
  useEffect(() => {
    const offSwitching = onTenantSwitching(() => {
      gate.invalidate(); purge(); setError(null); setRefreshing(false); setLoading(true);
      bump((n) => n + 1);
    });
    const offChange = onTenantChange(() => bump((n) => n + 1));
    return () => { offSwitching(); offChange(); };
  }, [gate, purge]);

  // One intended initial load per confirmed scope.
  useEffect(() => {
    if (!BS_SERVED) { void load(false); return; }
    if (!key) { setLoading(true); return; }
    void load(false);
  }, [key, load]);

  /** Explicit Retry / refresh. With identity failed or tenantless it re-asks for
   *  identity (one whoami); the trio follows only once a tenant is confirmed. */
  const refresh = useCallback(async () => {
    if (BS_SERVED && !keyRef.current) {
      const st = getTenantIdentityStatus();
      if (st === "failed" || st === "tenantless") { setError(null); retryTenantIdentity(); bump((n) => n + 1); }
      return;
    }
    if (BS_SERVED) setError(null);
    await load(true);
  }, [load]);

  const bind = (ticket?: WriteTicket): WriteTicket => {
    const t = ticket ?? gate.ticket();
    if (!t) throw new BookingScopeError("SCOPE_UNCONFIRMED");
    if (!t.live()) throw new BookingScopeError("SCOPE_CHANGED");
    return t;
  };
  const after = (t: WriteTicket) => { if (!t.live()) throw new BookingScopeError("SCOPE_CHANGED"); };
  /* Every await below goes through settleInScope, so a late answer from the old
     scope, success or failure, becomes SCOPE_CHANGED there. That is not enough on
     its own: settleInScope's check and this function's continuation are separate
     microtasks, and a switch can land between them. So each await is followed by
     after(t) directly before the state change, and the whole body runs inside
     scoped(), which re-normalises at this function's own catch. */
  const scoped = async <R,>(t: WriteTicket, body: () => Promise<R>): Promise<R> => {
    let r: R;
    try { r = await body(); } catch (e) { throw scopedError(t, e); }
    after(t);
    return r;
  };

  const upsert = (tp: BookingTemplate) => setTemplates((prev) => {
    const i = prev.findIndex((x) => x.id === tp.id);
    if (i < 0) return [...prev, tp];
    const next = prev.slice(); next[i] = tp; return next;
  });

  const reloadTemplates = async (t: WriteTicket) => {
    const read = op<{ data: BookingTemplate[] }>("listBookingTemplates");
    if (!read) return;
    after(t);
    await scoped(t, async () => {
      const tp = await settleInScope(t, read());
      after(t);
      setTemplates(Array.isArray(tp?.data) ? tp.data : []);
    });
  };

  /* What each write sends and returns is unchanged. Scope binding is the one
     addition: refuse to start outside a confirmed scope, and reject rather than
     resolve when the scope moved while the write was in flight, whether the
     request itself resolved or rejected. */
  const saveSettings = useCallback(async (patch: UpdateBookingSettingsInput, ticket?: WriteTicket) => {
    const f = op<{ data: BookingSettings }>("updateBookingSettings");
    if (!f) absent("updateBookingSettings");
    const t = bind(ticket);
    await scoped(t, async () => {
      const r = await settleInScope(t, f(patch as never));
      after(t);
      setSettings(r?.data ?? null);
    });
  }, [gate]);
  const saveTemplate = useCallback(async (id: string, patch: UpdateBookingTemplateInput, ticket?: WriteTicket) => {
    const f = op<{ data: BookingTemplate }>("updateBookingTemplate");
    if (!f) absent("updateBookingTemplate");
    const t = bind(ticket);
    return scoped(t, async () => {
      const r = await settleInScope(t, f(id as never, patch as never));
      after(t);
      upsert(r.data);
      // A shared threshold lands on another template; refetch the list so both halves agree.
      if (patch.thresholds) { await reloadTemplates(t); after(t); }
      return r.data;
    });
  }, [gate]);
  const createTemplate = useCallback(async (input: CreateBookingTemplateInput, ticket?: WriteTicket) => {
    const f = op<{ data: BookingTemplate }>("createBookingTemplate");
    if (!f) absent("createBookingTemplate");
    const t = bind(ticket);
    return scoped(t, async () => {
      const r = await settleInScope(t, f(input as never));
      after(t);
      upsert(r.data);
      return r.data;
    });
  }, [gate]);
  const setState = useCallback(async (id: string, state: BookingTemplateState, ticket?: WriteTicket) => {
    const f = op<{ data: BookingTemplate; archived: string[] }>("setBookingTemplateState");
    if (!f) absent("setBookingTemplateState");
    const t = bind(ticket);
    return scoped(t, async () => {
      const r = await settleInScope(t, f(id as never, state as never));
      after(t);
      await reloadTemplates(t);
      after(t);
      return { template: r.data, archived: r.archived ?? [] };
    });
  }, [gate]);
  const restore = useCallback(async (id: string, version: number, ticket?: WriteTicket) => {
    const f = op<{ data: BookingTemplate }>("restoreBookingTemplateVersion");
    if (!f) absent("restoreBookingTemplateVersion");
    const t = bind(ticket);
    return scoped(t, async () => {
      const r = await settleInScope(t, f(id as never, version as never));
      after(t);
      upsert(r.data);
      return r.data;
    });
  }, [gate]);

  const defLoc = settings?.general?.defaultLocale ?? "en-US";
  /* Both selectors take the array defensively. This is where the crash landed:
     `types` held `undefined` and `typeById` called `.find` on it. State is now
     never set to a non-array, and these still guard — two cheap checks against a
     dead route. */
  const liveFor = useCallback((typeId: string) => {
    const list = Array.isArray(templates) ? templates : [];
    return list.find((t) => t.typeId === typeId && t.locale === defLoc && t.state === "active")
      ?? list.find((t) => t.typeId === typeId && t.builtIn) ?? null;
  }, [templates, defLoc]);
  const typeById = useCallback((id: string) => {
    const list = Array.isArray(types) ? types : [];
    return list.find((t) => t.id === id) ?? null;
  }, [types]);

  const identityError = !BS_SERVED ? null : identity === "failed" ? BS_IDENTITY_FAILED : identity === "tenantless" ? BS_IDENTITY_TENANTLESS : null;
  const outLoading = identityError ? false : loading;
  const outError = identityError ?? error;
  const stale = !identityError && !!error && dataSlug !== null && BS_SERVED;
  const tenantSlug = dataSlug ?? scope.slug;
  const writable = BS_SERVED && !!key && dataSlug === scope.slug && !loading;
  const beginWrite = useCallback(() => (writable ? gate.ticket() : null), [writable, gate]);

  return useMemo(() => ({
    loading: outLoading, refreshing, error: outError, stale, identity, tenantSlug, scopeKey: key, writable, beginWrite,
    settings, types, templates, refresh, saveSettings, saveTemplate, createTemplate, setState, restore, liveFor, typeById,
  }), [outLoading, refreshing, outError, stale, identity, tenantSlug, key, writable, beginWrite, settings, types, templates, refresh, saveSettings, saveTemplate, createTemplate, setState, restore, liveFor, typeById]);
}

/* ── Pure helpers the panes share ─────────────────────────────────────── */

export const LOCALES: { value: string; label: string; short: string }[] = [
  { value: "en-US", get label() { return i18nT("bookingSettings.locale.enUS.label", "English (United States)"); }, get short() { return i18nT("bookingSettings.locale.enUS.short", "English (US)"); } },
  { value: "en-GB", get label() { return i18nT("bookingSettings.locale.enGB.label", "English (United Kingdom)"); }, get short() { return i18nT("bookingSettings.locale.enGB.short", "English (UK)"); } },
  { value: "en-AU", get label() { return i18nT("bookingSettings.locale.enAU.label", "English (Australia)"); }, get short() { return i18nT("bookingSettings.locale.enAU.short", "English (AU)"); } },
  { value: "fr-FR", get label() { return i18nT("bookingSettings.locale.frFR.label", "French (France)"); }, get short() { return i18nT("bookingSettings.locale.frFR.short", "French"); } },
  { value: "fr-CA", get label() { return i18nT("bookingSettings.locale.frCA.label", "French (Canada)"); }, get short() { return i18nT("bookingSettings.locale.frCA.short", "French (CA)"); } },
  { value: "es-ES", get label() { return i18nT("bookingSettings.locale.esES.label", "Spanish (Spain)"); }, get short() { return i18nT("bookingSettings.locale.esES.short", "Spanish"); } },
  { value: "es-MX", get label() { return i18nT("bookingSettings.locale.esMX.label", "Spanish (Mexico)"); }, get short() { return i18nT("bookingSettings.locale.esMX.short", "Spanish (MX)"); } },
  { value: "de-DE", get label() { return i18nT("bookingSettings.locale.deDE.label", "German (Germany)"); }, get short() { return i18nT("bookingSettings.locale.deDE.short", "German"); } },
  { value: "it-IT", get label() { return i18nT("bookingSettings.locale.itIT.label", "Italian (Italy)"); }, get short() { return i18nT("bookingSettings.locale.itIT.short", "Italian"); } },
  { value: "pt-BR", get label() { return i18nT("bookingSettings.locale.ptBR.label", "Portuguese (Brazil)"); }, get short() { return i18nT("bookingSettings.locale.ptBR.short", "Portuguese"); } },
  { value: "nl-NL", get label() { return i18nT("bookingSettings.locale.nlNL.label", "Dutch (Netherlands)"); }, get short() { return i18nT("bookingSettings.locale.nlNL.short", "Dutch"); } },
  { value: "pl-PL", get label() { return i18nT("bookingSettings.locale.plPL.label", "Polish (Poland)"); }, get short() { return i18nT("bookingSettings.locale.plPL.short", "Polish"); } },
  { value: "ar-AE", get label() { return i18nT("bookingSettings.locale.arAE.label", "Arabic (UAE)"); }, get short() { return i18nT("bookingSettings.locale.arAE.short", "Arabic"); } },
  { value: "zh-CN", get label() { return i18nT("bookingSettings.locale.zhCN.label", "Chinese (Simplified)"); }, get short() { return i18nT("bookingSettings.locale.zhCN.short", "Chinese"); } },
  { value: "ja-JP", get label() { return i18nT("bookingSettings.locale.jaJP.label", "Japanese (Japan)"); }, get short() { return i18nT("bookingSettings.locale.jaJP.short", "Japanese"); } },
];
export const localeShort = (c: string) => LOCALES.find((l) => l.value === c)?.short ?? c;
export const localeLabel = (c: string) => LOCALES.find((l) => l.value === c)?.label ?? c;

/** "Customer - Appointment - Confirmation" → "Confirmation" (the nav group already says the rest). */
export const leafName = (n: string) => { const p = n.split(" - "); return p.length > 2 ? p.slice(2).join(" - ") : n; };

export type OffsetUnit = "Minutes" | "Hours" | "Days";
export const UNIT_MINUTES: Record<OffsetUnit, number> = { Minutes: 1, Hours: 60, Days: 1440 };
export const UNIT_OPTIONS: Record<OffsetUnit, number[]> = { Minutes: [0, 5, 10, 15, 30, 45], Hours: [1, 2, 3, 4, 6, 12, 24, 48], Days: [1, 2, 3, 5, 7, 14] };
export function splitMinutes(m: number): { n: number; unit: OffsetUnit } {
  if (m === 0) return { n: 0, unit: "Minutes" };
  if (m % 1440 === 0) return { n: m / 1440, unit: "Days" };
  if (m % 60 === 0) return { n: m / 60, unit: "Hours" };
  return { n: m, unit: "Minutes" };
}
export const joinMinutes = (n: number, unit: OffsetUnit) => n * UNIT_MINUTES[unit];

export const HALF_HOURS: string[] = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);
export function hm12(hm: string): string {
  const [h, m] = hm.split(":").map((x) => parseInt(x, 10));
  const ap = h < 12 ? "AM" : "PM"; const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
}

export const CHANNEL_LABEL: Record<string, string> = {
  email: "Email", sms: "SMS", whatsapp: "WhatsApp", rcs: "RCS", teams: "Microsoft Teams", slack: "Slack", gchat: "Google Chat",
};
